import { useMemo } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { useTranslation } from 'react-i18next'
import { Bar } from 'react-chartjs-2'
import { useSelector } from 'react-redux'

import GraphContainer from '../GraphContainer'

import { socialReturnOnInvestmentReportSelector } from '../../../../../modules/selectors/socialReturnOnInvestment'
import { selectedPartnershipCurrency } from '../../../../../modules/selectors/partnerships'

import { getOptions } from './options'
import { insertIf } from '../../../../../helpers/common'
import { getHtmlLegendPlugin } from '../../../plugins'
import { formatNumberToShort } from '../../../helpers'

const TotalSocialImpactByRoleGraph = () => {
  const { t } = useTranslation()

  const { currency_symbol: currencySymbol } = useSelector(selectedPartnershipCurrency)
  const { categoricalImpact, totalImpact } = useSelector(socialReturnOnInvestmentReportSelector)

  const impactGraphLabels = useMemo(() => {
    return [
      ...insertIf(categoricalImpact?.Players, 'Players'),
      ...insertIf(categoricalImpact?.Attendees, 'Attendees'),
      ...insertIf(categoricalImpact?.Viewers, 'Fans')
    ]
  }, [categoricalImpact])

  const impactGraphDatasets = useMemo(() => {
    const healthImpactGraphData = [
      ...insertIf(categoricalImpact?.Players, categoricalImpact?.Players?.healthImpact),
      ...insertIf(categoricalImpact?.Attendees, categoricalImpact?.Attendees?.healthImpact),
      ...insertIf(categoricalImpact?.Viewers, categoricalImpact?.Viewers?.healthImpact)
    ]

    const wellBeingImpactGraphData = [
      ...insertIf(categoricalImpact?.Players, categoricalImpact?.Players?.wellbeingImpact),
      ...insertIf(categoricalImpact?.Attendees, categoricalImpact?.Attendees?.wellbeingImpact),
      ...insertIf(categoricalImpact?.Viewers, categoricalImpact?.Viewers?.wellbeingImpact)
    ]

    const economicImpactGraphData = [
      ...insertIf(categoricalImpact?.Players, categoricalImpact?.Players?.financialImpact),
      ...insertIf(categoricalImpact?.Attendees, categoricalImpact?.Attendees?.financialImpact),
      ...insertIf(categoricalImpact?.Viewers, categoricalImpact?.Viewers?.financialImpact)
    ]

    return [
      {
        label: 'Health',
        data: healthImpactGraphData,
        backgroundColor: '#C6D2FD',
        borderSkipped: false,
        maxBarThickness: 45
      },
      {
        label: 'Wellbeing',
        data: wellBeingImpactGraphData,
        backgroundColor: '#C893FD',
        borderSkipped: false,
        maxBarThickness: 45
      },
      {
        label: 'Economic',
        data: economicImpactGraphData,
        backgroundColor: '#F0E5FC',
        borderRadius: {
          topLeft: 0,
          topRight: 4,
          bottomLeft: 0,
          bottomRight: 4
        },

        borderSkipped: false,
        maxBarThickness: 45
      }
    ]
  }, [categoricalImpact])

  const legendContainerId = useMemo(() => uuidv4(), [])

  const options = useMemo(() => getOptions(legendContainerId, currencySymbol), [legendContainerId, currencySymbol])

  const graphData = useMemo(
    () => ({
      labels: impactGraphLabels,
      datasets: impactGraphDatasets
    }),
    [impactGraphDatasets, impactGraphLabels]
  )

  const plugins = useMemo(() => {
    const htmlLegendPlugin = getHtmlLegendPlugin()

    return [htmlLegendPlugin]
  }, [])

  const formattedTotalImpact = useMemo(() => {
    return `${currencySymbol}${formatNumberToShort(totalImpact)}`
  }, [currencySymbol, totalImpact])

  return (
    <GraphContainer
      title={t('Total Social Impact - by Impact Pathway')}
      totalValue={formattedTotalImpact}
      legendContainerId={legendContainerId}
    >
      <Bar type="bar" data={graphData} options={options} plugins={plugins} />
    </GraphContainer>
  )
}

export default TotalSocialImpactByRoleGraph
